import { useAuth0 } from '@auth0/auth0-react'
import { useDisclosure, useToast } from '@chakra-ui/react'
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import CloseAccountModal from '../../../../components/CloseAccountModal'
import PersonalDetailItem from '../../../../components/PersonalDetailItem'
import { AUTH0_LOGOUT_URL } from '../../../../constants'
import {
  useCloseAccountMutation,
  useGetAllMembersByOrganizationIdQuery,
  useGetUserQuery,
} from '../../../../redux/services/coreApi'
import { clearImpersonationSnapshot } from '../../../../redux/store'
import type { RootState } from '../../../../redux/store'
import Member from './components/Member'
import AddMemberModal from './containers/AddMemberModal'
import DisableMfaModal from './containers/DisableMfaModal'

function ProfileScreen() {
  const addMemberModal = useDisclosure()
  const closeAccountModal = useDisclosure()
  const disableMfaModal = useDisclosure()
  const history = useHistory()
  const toast = useToast()

  const sessionUserType = useSelector((state: RootState) => state.session.role)
  const { logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0()

  const sessionUser = useSelector((state: RootState) => state.session.user)
  const { data: user } = useGetUserQuery(sessionUser.id)

  const members = useGetAllMembersByOrganizationIdQuery(
    user
      ? {
          organizationId: user.id,
        }
      : skipToken,
    {
      skip: user?.role !== 'Organization',
    }
  )

  const isOrganization = sessionUserType === 'Organization'

  useEffect(() => {
    const getAccessToken = async () => {
      // biome-ignore lint/suspicious/noConsole: <only happens in dev>
      console.debug('Bearer ' + (await getAccessTokenSilently()))
    }

    if (import.meta.env.DEV) {
      getAccessToken()
    }
  }, [])

  const isMfaEnabled = user?.mfaEnabled

  const [closeAccount, { isLoading: isClosingAccount }] =
    useCloseAccountMutation()

  const handleCloseAccount = async () => {
    try {
      await closeAccount({ confirm: true }).unwrap()
      toast({
        title: 'Your account has been closed',
        description: 'You are being logged out.',
        status: 'success',
        duration: 3000,
      })
      // The account can no longer authenticate; log the customer out.
      clearImpersonationSnapshot()
      logout({ returnTo: AUTH0_LOGOUT_URL })
    } catch {
      toast({
        title: 'Could not close your account',
        description: 'Please try again or contact support.',
        status: 'error',
        duration: 4000,
      })
    }
  }

  return (
    <div className="pb-40">
      <AddMemberModal
        isOpen={addMemberModal.isOpen}
        onClose={addMemberModal.onClose}
      />
      <CloseAccountModal
        isOpen={closeAccountModal.isOpen}
        onClose={closeAccountModal.onClose}
        isLoading={isClosingAccount}
        onConfirm={handleCloseAccount}
      />
      <DisableMfaModal
        isOpen={disableMfaModal.isOpen}
        onClose={disableMfaModal.onClose}
        onDisabled={() => {
          disableMfaModal.onClose()
          toast({
            title: 'Two-factor authentication disabled!',
            description: 'You can now log in without MFA',
            status: 'success',
            duration: 2000,
          })
        }}
      />
      <h1 className="h1">Profile</h1>

      <h2 className="h2 mb-2">Account overview</h2>
      <div className="card gap-2 md:gap-4 mb-5">
        <PersonalDetailItem title="Account type" value={user?.role} />
        <PersonalDetailItem
          title={`${isOrganization ? 'KYB status' : 'KYC status'}`}
          value={user?.kycDetails?.kycStatus ?? 'Not verified'}
        />
        <PersonalDetailItem title="Email" value={user?.email || ''} />
        <PersonalDetailItem title="Name" value={user?.name} />
        <PersonalDetailItem title="Phone" value={user?.phone || ''} />
      </div>

      <h2 className="h2 mt-6">Security</h2>
      <div className="card gap-2 md:gap-4 mb-5">
        <PersonalDetailItem
          title="MFA Status"
          value={isMfaEnabled ? 'Enabled' : 'Disabled'}
        />

        {user?.mfaEnabled ? (
          <button className="button-danger" onClick={disableMfaModal.onOpen}>
            Disable MFA
          </button>
        ) : (
          <button
            className="approve-button"
            onClick={() => history.push('/app/mfa/enroll')}
          >
            Enroll in MFA
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 mt-10">
        {isOrganization && (
          <>
            <h2 className="h2">Users & Owners</h2>
            <button
              onClick={addMemberModal.onOpen}
              className="text-brand-accent dark:text-white cursor-pointer"
            >
              <FontAwesomeIcon
                icon={faCircleXmark}
                size="lg"
                className="rotate-45"
              />
            </button>
          </>
        )}
      </div>
      {isOrganization && (
        <div className="card gap-5">
          {members && members.data?.length === 0 && (
            <div className="flex justify-center text-description">
              You have no members in your organization yet.
            </div>
          )}
          {members &&
            members.data?.map(member => {
              return <Member key={member.id} member={member} />
            })}
        </div>
      )}

      <h2 className="h2 mt-10">Close account</h2>
      <div className="card gap-2 md:gap-4 mb-5">
        <p className="text-description">
          Permanently close your account. You will be logged out and will no
          longer be able to log in. This cannot be undone.
        </p>
        <div>
          <button
            className="button-danger"
            onClick={closeAccountModal.onOpen}
          >
            Close my account
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProfileScreen
